import { AccountType } from '@lune-climate/lune'
import { InputNew, LoadingWrapper, Text } from '@lune-fe/lune-ui-lib'
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined'
import Box from '@mui/material/Box'
import { Dispatch, FC, SetStateAction, useEffect, useState } from 'react'

import FilterItem from 'components/FilterItem'
import { ACCOUNT_ID_QUERY_PARAM_KEY } from 'components/FiltersWithQueryParamsWrapper'
import { getAccountsById, getRecentlyUsedAccounts } from 'endpoints/dapi'
import useAccounts from 'hooks/useAccounts'
import useIsTestMode from 'hooks/useIsTestMode'
import useRecentlyUsedAccounts from 'hooks/useRecentlyUsedAccounts'
import useUpdateEffect from 'hooks/useUpdateEffect'

interface AccountMultiselectFilterProps {
    setSelectedIds: Dispatch<SetStateAction<string[]>>
    preselectedIds: string[]
}

interface AccountMini {
    name: string
    id: string
}

function removeSelected(options: AccountMini[], selected: AccountMini[]) {
    const selectedIds = selected.map((s) => s.id)
    return options.filter((o) => !selectedIds.includes(o.id))
}

const AccountMultiselectFilter: FC<AccountMultiselectFilterProps> = ({
    setSelectedIds,
    preselectedIds,
}) => {
    const isTestMode = useIsTestMode()
    const { activeAccount } = useAccounts()

    const [searchTerm, setSearchTerm] = useState('')
    const [accounts, setAccounts] = useState<AccountMini[]>([])
    const [initialAccounts, setInitialAccounts] = useState<AccountMini[]>([])
    const [localSelectedAccounts, setLocalSelectedAccounts] = useState<AccountMini[]>([])
    const [localCheckboxItems, setLocalCheckboxItems] = useState<
        { account: AccountMini; checked: boolean }[]
    >([])
    const [loadingInitialState, setLoadingInitialState] = useState<boolean>(true)

    const { recentlyUsedAccounts } = useRecentlyUsedAccounts()

    useEffect(() => {
        if (recentlyUsedAccounts) {
            setInitialAccounts(recentlyUsedAccounts)
        }
    }, [recentlyUsedAccounts])

    useEffect(() => {
        const params = new URLSearchParams(window.location.search)
        const paramAccountIds = params.getAll(ACCOUNT_ID_QUERY_PARAM_KEY)

        const getAccountData = async () => {
            const accounts = await getAccountsById({ accountId: preselectedIds })
            setLocalSelectedAccounts(accounts)
            setLoadingInitialState(false)
        }

        if (paramAccountIds[0] === 'none') {
            setLocalSelectedAccounts([])
            setLoadingInitialState(false)
        } else {
            getAccountData()
        }
        // We only want to run this on initial render
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useUpdateEffect(() => {
        if (activeAccount && !loadingInitialState) {
            const active = { name: activeAccount.name, id: activeAccount.id }
            setAccounts([active, ...removeSelected(initialAccounts, [active])].slice(0, 5))
            setLocalSelectedAccounts([active])
            setSelectedIds([activeAccount.id])
        }
    }, [activeAccount])

    useUpdateEffect(() => {
        setAccounts(
            [
                ...localSelectedAccounts,
                ...removeSelected(initialAccounts, localSelectedAccounts),
            ].slice(0, 5),
        )
    }, [initialAccounts])

    // Update checkbox items when accounts or selected accounts change
    useEffect(() => {
        const items = accounts.map((account) => ({
            account,
            checked: localSelectedAccounts.some((selected) => selected.id === account.id),
        }))
        setLocalCheckboxItems(items)
    }, [accounts, localSelectedAccounts])

    function handleChange(selectedItem: { account: AccountMini; checked: boolean }): void {
        setLocalSelectedAccounts((prevSelected) => {
            const isCurrentlySelected = prevSelected.some(
                (acc) => acc.id === selectedItem.account.id,
            )

            let newSelected: AccountMini[]
            if (isCurrentlySelected) {
                // Remove from selection
                newSelected = prevSelected.filter((acc) => acc.id !== selectedItem.account.id)
            } else {
                // Add to selection
                newSelected = [...prevSelected, selectedItem.account]
            }

            setSelectedIds(newSelected.map((acc) => acc.id))

            // Update checkbox items to reflect the new selection
            setLocalCheckboxItems((oldItems) =>
                oldItems.map((item) => ({
                    ...item,
                    checked: newSelected.some((acc) => acc.id === item.account.id),
                })),
            )

            return newSelected
        })
    }

    function handleInputChange(value: string) {
        setSearchTerm(value)
        if (value === '') {
            setAccounts(
                [
                    ...localSelectedAccounts,
                    ...removeSelected(initialAccounts, localSelectedAccounts),
                ].slice(0, 5),
            )
        } else {
            getRecentlyUsedAccounts({
                name: value,
                type: isTestMode ? AccountType.TEST : AccountType.LIVE,
            }).then((res) => setAccounts(res))
        }
    }

    return (
        <>
            <Box
                sx={{
                    padding: '8px 16px',
                    position: 'sticky',
                    top: 0,
                    backgroundColor: 'white',
                    zIndex: 1,
                    pt: 3 / 2,
                }}
            >
                <InputNew
                    wide
                    size={'sm'}
                    placeholder="Search client accounts"
                    value={searchTerm}
                    onChange={(e) => handleInputChange(e.target.value)}
                    onKeyDown={(e) => e.stopPropagation()}
                    startAdornment={<SearchOutlinedIcon />}
                />
            </Box>
            {!searchTerm && (
                <Text
                    variant={'body2'}
                    sx={{
                        color: `TextSecondary`,
                        pl: 2,
                        mt: 1,
                        mb: 1,
                    }}
                >
                    Recently used:
                </Text>
            )}
            <LoadingWrapper
                loading={!localCheckboxItems.length && !searchTerm}
                sx={{ minHeight: '200px' }}
            >
                {localCheckboxItems.map((filter) => (
                    <FilterItem
                        key={filter.account.id}
                        checked={filter.checked}
                        label={filter.account.name}
                        onToggle={() => handleChange(filter)}
                    />
                ))}
            </LoadingWrapper>
        </>
    )
}

export default AccountMultiselectFilter
